import { useState, useEffect, useCallback } from 'react'
import { DoorQuestQuestion, DoorQuestGameState, DoorQuestGameConfig } from '../types'
import { getGameQuestions, getGameQuestionConfig, hasGameQuestions } from '../data/gameQuestions'

/**
 * Hook quản lý logic và state của DoorQuest Game
 */
export const useDoorQuestGame = (config: DoorQuestGameConfig) => {
  const {
    minQuestions = 6,
    maxQuestions = 10,
    lop,
    chuong,
    bai
  } = config

  const [gameState, setGameState] = useState<DoorQuestGameState>({
    currentQuestionIndex: 0,
    score: 0,
    totalQuestions: 0,
    questions: [],
    isGameActive: false,
    isGameWon: false,
    isGameLost: false,
    selectedDoor: null,
    showFeedback: false,
    feedbackMessage: '',
    feedbackType: null,
    isTransitioning: false, // Đang chuyển phòng
    roomOffset: 0 // Offset Z để render multiple rooms
  })

  const [isLoading, setIsLoading] = useState(false)

  /**
   * Load câu hỏi đơn giản từ gameQuestions
   */
  const loadQuestions = useCallback(async () => {
    if (!lop || !chuong || !bai) {
      console.error('❌ Missing lesson information')
      return
    }

    setIsLoading(true)
    try {
      console.log('🎮 Loading simple questions for DoorQuest game...')
      console.log(`📚 Lesson: Lớp ${lop} - Chương ${chuong} - Bài ${bai}`)
      
      // Lấy câu hỏi đơn giản từ data
      const allQuestions = getGameQuestions(lop, chuong, bai)
      const customConfig = getGameQuestionConfig(lop, chuong, bai)

      const effectiveMinQuestions = Math.max(
        1,
        customConfig?.minQuestions ?? minQuestions
      )
      let effectiveMaxQuestions = customConfig?.maxQuestions ?? maxQuestions
      if (typeof effectiveMaxQuestions !== 'number' || effectiveMaxQuestions < effectiveMinQuestions) {
        effectiveMaxQuestions = effectiveMinQuestions
      }

      console.log(`✅ Found ${allQuestions.length} simple questions`)

      if (allQuestions.length < effectiveMinQuestions) {
        console.warn(`⚠️ Not enough questions. Need at least ${effectiveMinQuestions}, got ${allQuestions.length}`)
        throw new Error(
          `Bài học này chưa có đủ câu hỏi game.\n` +
          `Cần tối thiểu ${effectiveMinQuestions} câu, hiện có ${allQuestions.length} câu.\n` +
          `Vui lòng chơi bài học khác hoặc làm trắc nghiệm.`
        )
      }

      // Random và lấy số lượng câu hỏi cần thiết
      const questionRange = effectiveMaxQuestions - effectiveMinQuestions + 1
      const randomizedCount = questionRange > 0
        ? Math.floor(Math.random() * questionRange) + effectiveMinQuestions
        : effectiveMinQuestions
      const questionCount = Math.min(randomizedCount, allQuestions.length)

      // Shuffle câu hỏi
      const shuffled = [...allQuestions].sort(() => Math.random() - 0.5)
      const selectedQuestions = shuffled.slice(0, questionCount)

      // Xáo trộn đáp án cho mỗi câu hỏi
      const questionsWithShuffledAnswers = selectedQuestions.map(q => {
        // Tạo mảng index [0, 1, 2] và xáo trộn
        const indices = [0, 1, 2]
        const shuffledIndices = [...indices].sort(() => Math.random() - 0.5)
        
        // Tạo mảng đáp án mới theo thứ tự xáo trộn
        const shuffledAnswers = shuffledIndices.map(i => q.answers[i])
        
        // Tìm vị trí mới của đáp án đúng
        const newCorrectIndex = shuffledIndices.indexOf(q.correctIndex)
        
        return {
          ...q,
          answers: shuffledAnswers,
          correctIndex: newCorrectIndex
        }
      })

      console.log(`🎯 Selected ${questionsWithShuffledAnswers.length} questions with shuffled answers`)

      setGameState(prev => ({
        ...prev,
        questions: questionsWithShuffledAnswers,
        totalQuestions: questionsWithShuffledAnswers.length
      }))

    } catch (error) {
      console.error('❌ Error loading questions:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [lop, chuong, bai, minQuestions, maxQuestions])

  /**
   * Bắt đầu game
   */
  const startGame = useCallback(async () => {
    try {
      await loadQuestions()
      setGameState(prev => ({
        ...prev,
        isGameActive: true,
        isGameWon: false,
        isGameLost: false,
        currentQuestionIndex: 0,
        score: 0,
        selectedDoor: null,
        showFeedback: false,
        feedbackMessage: '',
        feedbackType: null
      }))
    } catch (error) {
      console.error('Error starting game:', error)
    }
  }, [loadQuestions])

  /**
   * Xử lý khi người chơi chọn cửa
   */
  const selectDoor = useCallback((doorIndex: number) => {
    if (!gameState.isGameActive || gameState.showFeedback) return

    const currentQuestion = gameState.questions[gameState.currentQuestionIndex]
    const isCorrect = doorIndex === currentQuestion.correctIndex

    setGameState(prev => ({
      ...prev,
      selectedDoor: doorIndex,
      showFeedback: true,
      feedbackType: isCorrect ? 'correct' : 'incorrect',
      feedbackMessage: isCorrect 
        ? 'Chúc mừng, cánh cửa đã mở. Mời bạn vào.' 
        : 'Cửa đã đóng, về nhà học bài lại nhé bạn!'
    }))

    // Sau 1.5s, bắt đầu transition (nhân vật đi qua cửa)
    setTimeout(() => {
      if (isCorrect) {
        setGameState(prev => ({
          ...prev,
          isTransitioning: true
        }))
        
        // Sau 3s nữa (tổng 4.5s), hoàn thành transition và chuyển phòng
        setTimeout(() => {
          const nextIndex = gameState.currentQuestionIndex + 1
          const newScore = gameState.score + 1

          if (nextIndex >= gameState.totalQuestions) {
            // Hoàn thành tất cả câu hỏi - Chiến thắng!
            setGameState(prev => ({
              ...prev,
              isGameActive: false,
              isGameWon: true,
              score: newScore,
              isTransitioning: false
            }))
          } else {
            // Chuyển sang phòng tiếp theo
            setGameState(prev => ({
              ...prev,
              currentQuestionIndex: nextIndex,
              score: newScore,
              selectedDoor: null,
              showFeedback: false,
              feedbackMessage: '',
              feedbackType: null,
              isTransitioning: false,
              roomOffset: prev.roomOffset - 35 // Mỗi phòng dài 35m theo Z
            }))
          }
        }, 3000)
      } else {
        // Sai: Game over
        setTimeout(() => {
          setGameState(prev => ({
            ...prev,
            isGameActive: false,
            isGameLost: true
          }))
        }, 1000)
      }
    }, 1500)
  }, [gameState])

  /**
   * Reset game
   */
  const resetGame = useCallback(() => {
    setGameState({
      currentQuestionIndex: 0,
      score: 0,
      totalQuestions: 0,
      questions: [],
      isGameActive: false,
      isGameWon: false,
      isGameLost: false,
      selectedDoor: null,
      showFeedback: false,
      feedbackMessage: '',
      feedbackType: null,
      isTransitioning: false,
      roomOffset: 0
    })
  }, [])

  return {
    gameState,
    isLoading,
    startGame,
    selectDoor,
    resetGame,
    currentQuestion: gameState.questions[gameState.currentQuestionIndex]
  }
}

